import { ActionMenu } from 'common-ui/components/Table/ActionMenu'
import { ActionMenuRow } from 'common-ui/components/Table/ActionMenuContext'
import { useEffect, useState } from 'react'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Chip from '@mui/material/Chip'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import HistoryIcon from '@mui/icons-material/History'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useBillingApi } from '../../api/billing/billing-context'
import { InstallationRtuAllocation, InstallationRtuHistory } from 'common-billing-server/types'
import { formatDateTime } from '../../format-date'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'

interface InstallationRtuAllocationProps {
    installationId: string
    installationName: string
    readOnly: boolean
}

export const InstallationRtuAllocationComponent = ({
    installationId,
    installationName,
    readOnly,
}: InstallationRtuAllocationProps) => {
    const billingApi = useBillingApi()
    const [allocations, setAllocations] = useState<InstallationRtuAllocation[]>([])
    const [history, setHistory] = useState<InstallationRtuHistory[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isHistoryLoading, setIsHistoryLoading] = useState(false)
    const [showHistory, setShowHistory] = useState(false)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [selectedAllocation, setSelectedAllocation] = useState<InstallationRtuAllocation | null>(null)
    const [newQuantity, setNewQuantity] = useState(0)
    const [reference, setReference] = useState('')
    const [isUpdating, setIsUpdating] = useState(false)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    const fetchAllocations = async () => {
        const operationId = 'fetch-allocations'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            const response = await billingApi.listInstallationRtuAllocations(installationId)
            setAllocations(response.items)
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to fetch RTU allocations',
                error,
            })
        }
        setIsLoading(false)
    }

    const fetchHistory = async () => {
        const operationId = 'fetch-history'
        removeFailedOperation(operationId)
        setIsHistoryLoading(true)
        try {
            const response = await billingApi.listInstallationRtuAllocationHistories(installationId)
            setHistory(response.items)
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to fetch allocation history',
                error,
            })
        }
        setIsHistoryLoading(false)
    }

    useEffect(() => {
        void fetchAllocations()

        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [installationId])

    useEffect(() => {
        if (showHistory && history.length === 0) {
            void fetchHistory()
        }

        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [showHistory])

    const handleEditClick = (allocation: InstallationRtuAllocation) => {
        if (readOnly) return
        setSelectedAllocation(allocation)
        setNewQuantity(allocation.thisInstallation)
        setReference('')
        setIsDialogOpen(true)
    }

    const handleUpdateAllocation = async () => {
        if (!selectedAllocation || readOnly || isUpdating) return

        const operationId = 'update-allocation'
        removeFailedOperation(operationId)
        setIsUpdating(true)
        try {
            await billingApi.setInstallationRtuAllocation(installationId, {
                rtuTypeId: selectedAllocation.rtuType.id,
                quantity: newQuantity,
                reference,
            })
            setIsDialogOpen(false)
            await fetchAllocations()
            if (showHistory) {
                await fetchHistory()
            }
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed to update RTU allocation',
                error,
            })
        }
        setIsUpdating(false)
    }

    const getMaxAllowable = () => {
        if (!selectedAllocation) return 0
        return selectedAllocation.thisInstallation + selectedAllocation.available
    }

    return (
        <Paper style={{ padding: '20px' }}>
            <Typography variant="h6" gutterBottom>
                RTU Allocation for {installationName}
            </Typography>

            {errorBanner}
            {readOnly && <Alert severity="info">RTU allocation is read-only for this installation.</Alert>}

            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>RTU Type</TableCell>
                                    <TableCell align="right">Customer Total</TableCell>
                                    <TableCell align="right">Total Allocated</TableCell>
                                    <TableCell align="right">This Installation</TableCell>
                                    <TableCell align="right">Available</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allocations.map((allocation) => (
                                    <ActionMenuRow key={allocation.rtuType.id}>
                                        <TableCell>{allocation.rtuType.name}</TableCell>
                                        <TableCell align="right">{allocation.customerTotal}</TableCell>
                                        <TableCell align="right">{allocation.totalAllocated}</TableCell>
                                        <TableCell align="right">
                                            <strong>{allocation.thisInstallation}</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                label={allocation.available}
                                                color={allocation.available > 0 ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <ActionMenu
                                                options={[
                                                    {
                                                        label: 'Edit RTU allocation',
                                                        icon: <EditIcon />,
                                                        disabled: readOnly || allocation.customerTotal === 0,
                                                        onClick: () => handleEditClick(allocation),
                                                    },
                                                ]}
                                            >
                                                <IconButton
                                                    aria-label="Edit RTU allocation"
                                                    onClick={() => handleEditClick(allocation)}
                                                    size="small"
                                                    disabled={readOnly || allocation.customerTotal === 0}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </ActionMenu>
                                        </TableCell>
                                    </ActionMenuRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3 }}>
                        <Button
                            startIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            endIcon={<HistoryIcon />}
                            onClick={() => setShowHistory(!showHistory)}
                        >
                            {showHistory ? 'Hide' : 'Show'} Allocation History
                        </Button>
                    </Box>

                    <Collapse in={showHistory}>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Allocation History
                            </Typography>
                            {isHistoryLoading ? (
                                <CircularProgress size={20} />
                            ) : history.length === 0 ? (
                                <Alert severity="info">No allocation history found</Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>RTU Type</TableCell>
                                                <TableCell>Operation</TableCell>
                                                <TableCell>Quantity</TableCell>
                                                <TableCell>Reference</TableCell>
                                                <TableCell>User</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {history.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{formatDateTime(new Date(item.createdAt))}</TableCell>
                                                    <TableCell>{item.rtuType.name}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={item.operation}
                                                            color={
                                                                item.operation === 'allocate' ? 'success' : 'warning'
                                                            }
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{item.quantityChange}</TableCell>
                                                    <TableCell>{item.reference || '-'}</TableCell>
                                                    <TableCell>{item.createdBy}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Collapse>
                </>
            )}

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update RTU Allocation - {selectedAllocation?.rtuType.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info">Available to allocate: {selectedAllocation?.available || 0} RTUs</Alert>
                    </Box>
                    <TextField
                        fullWidth
                        label="Quantity"
                        disabled={readOnly || isUpdating}
                        type="number"
                        value={newQuantity}
                        onChange={(e) =>
                            setNewQuantity(Math.max(0, Math.min(getMaxAllowable(), parseInt(e.target.value) || 0)))
                        }
                        margin="normal"
                        required
                        helperText={`Current: ${selectedAllocation?.thisInstallation || 0}, Max: ${getMaxAllowable()}`}
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                max: getMaxAllowable(),
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Reference/Reason (optional)"
                        disabled={readOnly || isUpdating}
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        margin="normal"
                        multiline
                        rows={2}
                        helperText="Provide a reason for this allocation change"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDialogOpen(false)} disabled={isUpdating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleUpdateAllocation()}
                        variant="contained"
                        disabled={readOnly || isUpdating || newQuantity === selectedAllocation?.thisInstallation}
                        startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
